import { useNavigate, useSearchParams } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import React, { useEffect, useMemo, useState } from 'react';
import {
  HeaderSection,
  AppTable,
  useFetchList,
  PAGE_SIZE_OPTIONS,
  IAppTableColumn,
} from '@qlibs/react-components';
import { Actions, IAction } from '@qlibs/react-components/dist/Table';
import ListFilterRow from '../../components/ListFilterRow';
import { readListFilter, readListPage } from '../../helpers/listFilterQuery';
import {
  Button,
  Dropdown,
  Grid,
  Image,
  MenuProps,
  Modal,
  Tag,
  message,
} from 'antd';
import { DeviceProperties } from './types/device.type';
import { httpRequest } from '../../helpers/api';
import LinkTable from '../../components/LinkTable';
import useCompanies from '../../zustand/useGroup';
import useMasterData from '../../hooks/useMasterData';
import {
  EFormatCurrentDate,
  formatCurrentDate,
} from '../../helpers/formatCurrentDate';
import { isRamus2DeviceId } from '../../helpers/ramus2';
import useGroup from '../../zustand/useGroup';
import { EAEFuelType } from './Create';
import { RiEqualizer2Line, RiMore2Line } from '@remixicon/react';
import {
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  DeviceMobileCard,
  DeviceMobileCardBody,
  DeviceMobileCardMain,
  DeviceMobileCardSubtitle,
  DeviceMobileCardTitle,
  DeviceMobileDataLogButton,
  DeviceMobileDetailButton,
  DeviceMobileEmptyState,
  DeviceMobileFilterActions,
  DeviceMobileFilterApplyButton,
  DeviceMobileFilterButton,
  DeviceMobileFilterDrawer,
  DeviceMobileFilterField,
  DeviceMobileFilterFields,
  DeviceMobileFilterForm,
  DeviceMobileFilterRow,
  DeviceMobileFilterSelect,
  DeviceMobileIconButton,
  DeviceMobileInfoGrid,
  DeviceMobileInfoItem,
  DeviceMobileInfoLabel,
  DeviceMobileInfoValue,
  DeviceMobileList,
  DeviceMobileMoreButton,
  DeviceMobilePage,
  DeviceMobilePageButton,
  DeviceMobilePagination,
  DeviceMobilePaginationActions,
  DeviceMobilePaginationInfo,
  DeviceMobileSearch,
  MobileDeviceHeader,
} from './mobile';

const FEATURE = 'DEVICE';

const Device = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { isUserHasPermission } = usePermission();
  const { company } = useCompanies();
  const { filter, setFilter } = useGroup();
  const {
    getListCompanies,
    getListAssets,
    setAssets,
    companies,
    assets,
    loadingCompany,
    loadingAsset,
  } = useMasterData();

  const handleCreateDevice = () => {
    navigate({ pathname: '/device/add' });
  };

  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [isModalRestoreOpen, setIsModalRestoreOpen] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [mobileFilterValues, setMobileFilterValues] = useState({
    company: company?.companyId || 'all',
    asset: 'all',
    status: 'all',
    filterData: 'default',
  });
  const [currentData, setCurrentData] = useState<DeviceProperties>();
  const [forceDeleteId, setForceDeleteId] = useState<string>();
  const [companyId, setCompanyId] = useState<string>(company?.companyId);

  const {
    isLoading,
    data,
    pagination,
    query,
    setData,
    setQuery,
    changePage,
    changeLimit,
    handleSearch,
    pageQueries,
    filterDropdown,
  } = useFetchList<DeviceProperties>({
    httpRequest: httpRequest as any,
    endpoint: 'device',
    limit: +PAGE_SIZE_OPTIONS[1],
    initialQuery: {
      filterCompanyIds: readListFilter(
        'filterCompanyIds',
        company?.companyId || '',
      ),
      filterAssetIds: readListFilter('filterAssetIds'),
      filterStatus: readListFilter('filterStatus'),
      filterArchived: readListFilter('filterArchived'),
      search: readListFilter('search'),
    },
    pageQuery: {
      defaultValue: {
        page: readListPage(),
        filterCompanyIds: '',
        filterAssetIds: '',
        filterStatus: '',
        filterArchived: '',
        search: '',
      },
    },
  });

  async function deleteData() {
    try {
      if (currentData) {
        const forceDelete =
          forceDeleteId && forceDeleteId === currentData.deviceId;
        await httpRequest.delete('/device/' + currentData.deviceId, {
          params: {
            forceDelete: forceDelete || undefined,
          },
        });
        message.success(
          `Success ${forceDelete ? 'delete' : 'archive'} ${currentData.name}`
        );

        setQuery({
          ...query,
          // limit: +PAGE_SIZE_OPTIONS[1],
          // filterCompanyIds: company?.companyId || '',
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalDeleteOpen(false);
      setCurrentData(undefined);
    }
  }

  async function restoreData() {
    try {
      if (currentData) {
        await httpRequest.patch('/device/' + currentData.deviceId + '/restore');
        message.success(`Success restore ${currentData.name}`);

        setQuery({
          ...query,
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalRestoreOpen(false);
      setCurrentData(undefined);
      setForceDeleteId(undefined);
    }
  }

  async function handleRestoreDevice(id: any) {
    setIsModalRestoreOpen(true);
    const found = data.find((item) => item.deviceId === id);
    if (found) {
      setCurrentData(found);
    }
  }

  async function handleDeleteDevice(id: any, forced?: boolean) {
    setIsModalDeleteOpen(true);
    const found = data.find((item) => item.deviceId === id);
    if (found) {
      setCurrentData(found);
    }

    if (forced) {
      setForceDeleteId(id);
    }
  }

  function handleCancelDelete() {
    setIsModalDeleteOpen(false);
    setIsModalRestoreOpen(false);
    setCurrentData(undefined);
  }

  function isArchived(id?: string) {
    if (id) {
      const found = data.find((item) => item.deviceId === id && item.deletedAt);
      if (found) {
        return true;
      }
    }

    return false;
  }

  useEffect(() => {
    if (companyId && companyId !== 'all') {
      getListAssets({ filterCompanyIds: companyId });
    } else {
      setAssets([]);
    }
  }, [companyId]);

  useEffect(() => {
    getListCompanies();
  }, []);

  const filterAssetOptions = useMemo(() => {
    const data = assets.map((item) => ({
      value: item.assetId,
      label: item.name,
    }));
    return [{ value: 'all', label: 'All' }, ...data];
  }, [assets]);

  const [searchParams, setSearchParams] = useSearchParams();

  const handleViewDataLog = (device: DeviceProperties) => {
    setFilter({ ...filter, assetId: device.assetId });
    setSearchParams({
      tab: '2',
    });
    navigate('/device/' + device.deviceId + '?tab=2');
  };

  const getDeviceActions = (record: DeviceProperties): IAction[] => {
    const isReadOnlyRamus2 =
      record.isRamus2 || isRamus2DeviceId(record.deviceId);

    return [
      isUserHasPermission([FEATURE + '.DETAIL'])
        ? {
            key: 'detail',
            label: 'Detail',
            onPress: (id?: string) => navigate('/device/' + id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.UPDATE'])
        ? {
            key: 'update',
            label: 'Update',
            onPress: (id?: string) => navigate('/device/' + id + '/edit'),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.DELETE'])
        ? {
            key: 'archive',
            label: 'Delete',
            color: '#ff4d4f',
            disabled: (id?: string) => isArchived(id),
            disabledTooltip: 'This data is already archived',
            onPress: (id?: string) => handleDeleteDevice(id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.RESTORE'])
        ? {
            key: 'restore',
            label: 'Restore',
            color: '#1890ff',
            disabled: (id?: string) => !isArchived(id),
            disabledTooltip: 'This data not in archived',
            onPress: (id?: string) => handleRestoreDevice(id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.FORCE_DELETE'])
        ? {
            key: 'delete',
            label: 'Force Delete',
            onPress: (id?: string) => handleDeleteDevice(id, true),
          }
        : undefined,
    ].filter(Boolean) as IAction[];
  };

  const columns: IAppTableColumn<DeviceProperties>[] = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      type: 'detail',
      keyId: 'deviceId',
      render: (value, records) => (
        <LinkTable
          onPress={() => {
            setSearchParams({
              tab: '1',
            });
            navigate('/device/' + records.deviceId + '?tab=1');
          }}
        >
          {records.deletedAt ? (
            <Tag color='#ff5500' className='mb-2'>
              Archived
            </Tag>
          ) : (
            false
          )}
          {value}
        </LinkTable>
      ),
    },
    {
      title: 'ASSET',
      dataIndex: 'assetId',
      key: 'assetId',
      render: (_, records) => (
        <>
          {records.cloneData ? (
            <Tag color='#0762ff' className='mb-2'>
              Cloned
            </Tag>
          ) : (
            false
          )}
          <div>{records.cloneData?.name || records.asset?.name || 'N/A'}</div>
          {records.cloneData &&
          records.asset?.assetId !== records.cloneData.assetId ? (
            <div style={{ fontSize: 10, opacity: 0.5 }}>
              Original Asset: {records.asset?.name}
            </div>
          ) : (
            false
          )}
        </>
      ),
    },
    {
      title: 'DEVICE TYPE',
      dataIndex: 'deviceType',
      key: 'deviceType',
      render: (_, records) =>
        records.deviceType ? `${records.deviceType.name}` : 'N/A',
    },
    {
      title: 'DEVICE ID',
      dataIndex: 'uniqueId',
      key: 'uniqueId',
      render: (uniqueId, records) => (
        <div>
          <div>{uniqueId || 'N/A'}</div>
          {records.deviceType.name.toLowerCase().includes('ae') ? (
            !records.metadata ? (
              <Tag color={'error'}>AE Fuel Type Empty</Tag>
            ) : records.metadata.aeFuelType === EAEFuelType.FUEL_CONSTANTA &&
              !records.metadata.aeFuelConstanta ? (
              <Tag color={'error'}>Fuel Constant Empty</Tag>
            ) : (
              false
            )
          ) : (
            false
          )}
        </div>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (value, _) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'DATA LOG',
      key: 'dataLog',
      render: (_, records) =>
        isUserHasPermission([FEATURE + '.DETAIL']) ? (
          <Button
            size='small'
            type='primary'
            onClick={() => handleViewDataLog(records)}
          >
            Data Log
          </Button>
        ) : undefined,
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'ACTION',
      key: 'action',
      render: (_, record) => {
        const actions = getDeviceActions(record);

        if (!actions.length) {
          return null;
        }

        return <Actions id={record.deviceId} actions={actions} />;
      },
    },
  ];

  const selectedCompanyFilter =
    pageQueries.filterCompanyIds || companyId || 'all';
  const selectedAssetFilter = pageQueries.filterAssetIds || 'all';

  const filterCompanyOptions = useMemo(() => {
    const data = companies.map((item) => ({
      value: item.companyId,
      label: item.name,
    }));

    if (
      company?.companyId &&
      selectedCompanyFilter === company.companyId &&
      !data.some((item) => item.value === company.companyId)
    ) {
      data.unshift({
        value: company.companyId,
        label: company.name,
      });
    }

    return [{ value: 'all', label: 'All' }, ...data];
  }, [companies, company?.companyId, company?.name, selectedCompanyFilter]);

  const isCompanyFilterReady =
    loadingCompany === false &&
    (selectedCompanyFilter === 'all' ||
      filterCompanyOptions.some(
        (option) => option.value === selectedCompanyFilter,
      ));
  const isAssetFilterReady =
    loadingAsset === false &&
    (selectedAssetFilter === 'all' ||
      filterAssetOptions.some((option) => option.value === selectedAssetFilter));

  const filterData: any = [
    {
      type: 'search',
      key: 'search',
      label: 'Search',
      placeholder: 'Search by device name',
      onChange: (value: any) => {
        handleSearch(value);
      },
      colSpan: isUserHasPermission([FEATURE + '.DELETE']) ? 6 : 10,
    },
    {
      type: 'select',
      key: 'company',
      label: 'Company',
      options: filterCompanyOptions,
      onChange: (value: any) => {
        setCompanyId(value);
        filterDropdown({
          filterCompanyIds: value === 'all' ? '' : value,
          filterAssetIds: '',
        });
      },
      colSpan: 4,
      isHidden: !isCompanyFilterReady,
    },
    {
      type: 'select',
      key: 'asset',
      label: 'Filter Asset',
      options: filterAssetOptions,
      onChange: (value: any) => {
        filterDropdown({
          filterAssetIds: value === 'all' ? '' : value,
        });
      },
      colSpan: 4,
      isHidden: !isAssetFilterReady,
    },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: [
        {
          value: 'all',
          label: 'All',
        },
        {
          value: 'active',
          label: 'Active',
        },
        {
          value: 'inactive',
          label: 'Inactive',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterStatus: value === 'all' ? '' : value,
        });
      },
      colSpan: 4,
    },
    {
      type: 'select',
      key: 'filterData',
      label: 'Filter Data',
      options: [
        {
          value: 'default',
          label: 'Default',
        },
        {
          value: 'show',
          label: 'Include Archive Data',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterArchived: value === 'default' ? '' : value,
        });
      },
      colSpan: 4,
    },
  ].filter((item) => {
    if (item.isHidden) {
      return false;
    }

    return !isUserHasPermission([FEATURE + '.DELETE'])
      ? item.key !== 'filterData'
      : item;
  });

  const mobilePagination = {
    current: Number((pagination as any)?.page || pageQueries.page || 1),
    pageSize: Number(
      (pagination as any)?.limit || query.limit || +PAGE_SIZE_OPTIONS[1],
    ),
    total: Number((pagination as any)?.total || data.length),
  };
  const mobileCurrentPage = mobilePagination.current;
  const mobilePageSize = mobilePagination.pageSize;
  const mobileTotal = mobilePagination.total;
  const mobileTotalPages = Math.max(1, Math.ceil(mobileTotal / mobilePageSize));
  const mobileStartResult =
    mobileTotal === 0 ? 0 : (mobileCurrentPage - 1) * mobilePageSize + 1;
  const mobileEndResult = Math.min(
    mobileCurrentPage * mobilePageSize,
    mobileTotal,
  );

  const handleMobilePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > mobileTotalPages) {
      return;
    }

    changePage(nextPage, mobilePageSize);
  };

  React.useEffect(() => {
    if (!isMobileFilterOpen) {
      return;
    }

    setMobileFilterValues({
      company: selectedCompanyFilter,
      asset: selectedAssetFilter,
      status:
        (pageQueries as any).status ||
        (pageQueries as any).filterStatus ||
        'all',
      filterData:
        (pageQueries as any).filterData ||
        (pageQueries as any).filterArchived ||
        'default',
    });
  }, [
    isMobileFilterOpen,
    pageQueries,
    selectedAssetFilter,
    selectedCompanyFilter,
  ]);

  const handleApplyMobileFilter = () => {
    filterDropdown({
      filterCompanyIds:
        mobileFilterValues.company === 'all' ? '' : mobileFilterValues.company,
      filterAssetIds:
        mobileFilterValues.asset === 'all' ? '' : mobileFilterValues.asset,
      filterStatus:
        mobileFilterValues.status === 'all' ? '' : mobileFilterValues.status,
      filterArchived:
        mobileFilterValues.filterData === 'default'
          ? ''
          : mobileFilterValues.filterData,
    });
    setIsMobileFilterOpen(false);
  };

  const getMobileActionItems = (
    record: DeviceProperties,
  ): MenuProps['items'] => {
    return getDeviceActions(record)
      .filter(
        (
          action,
        ): action is Exclude<NonNullable<IAction>, string> =>
          Boolean(action) && typeof action !== 'string',
      )
      .map((action) => ({
        key: action.key,
        label: action.label,
        danger: action.color === '#ff4d4f',
        disabled: action.disabled?.(record.deviceId),
      }));
  };

  const handleMobileActionClick =
    (record: DeviceProperties) =>
    (info: MenuProps['onClick'] extends (arg: infer I) => any ? I : any) => {
      const action = getDeviceActions(record)
        .filter(
          (
            item,
          ): item is Exclude<NonNullable<IAction>, string> =>
            Boolean(item) && typeof item !== 'string',
        )
        .find((item) => item.key === info?.key);
      action?.onPress?.(record.deviceId);
    };

  return (
    <React.Fragment>
      {isMobile ? (
        <DeviceMobilePage>
          <MobileDeviceHeader
            title="Device"
            onBack={() => navigate(-1)}
            action={
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <DeviceMobileIconButton
                  type="button"
                  aria-label="Add device"
                  onClick={handleCreateDevice}
                >
                  <PlusOutlined />
                </DeviceMobileIconButton>
              ) : undefined
            }
          />

          <DeviceMobileFilterRow>
            <DeviceMobileSearch
              allowClear
              placeholder="Search by device name"
              defaultValue={pageQueries.search}
              suffix={<SearchOutlined />}
              onChange={(event) => handleSearch(event.target.value)}
            />
            <DeviceMobileFilterButton
              type="button"
              aria-label="Open device filters"
              onClick={() => setIsMobileFilterOpen(true)}
            >
              <RiEqualizer2Line />
            </DeviceMobileFilterButton>
          </DeviceMobileFilterRow>

          <DeviceMobileList>
            {isLoading ? (
              <DeviceMobileEmptyState>Loading devices...</DeviceMobileEmptyState>
            ) : !data.length ? (
              <DeviceMobileEmptyState>No device data found.</DeviceMobileEmptyState>
            ) : (
              data.map((device) => {
                const mobileActions = getMobileActionItems(device);

                return (
                  <DeviceMobileCard key={device.deviceId}>
                    <DeviceMobileCardMain>
                      <DeviceMobileCardBody>
                        <DeviceMobileCardTitle>
                          {device.deletedAt ? (
                            <Tag color="#ff5500" className="mb-2">
                              Archived
                            </Tag>
                          ) : (
                            false
                          )}
                          {device.name}
                        </DeviceMobileCardTitle>
                        <DeviceMobileCardSubtitle>
                          {device.uniqueId || 'N/A'}
                        </DeviceMobileCardSubtitle>
                        <DeviceMobileInfoGrid>
                          <DeviceMobileInfoItem>
                            <DeviceMobileInfoLabel>Asset</DeviceMobileInfoLabel>
                            <DeviceMobileInfoValue>
                              {device.cloneData ? (
                                <Tag color='#0762ff'>Cloned</Tag>
                              ) : (
                                false
                              )}
                              <div>
                                {device.cloneData?.name ||
                                  device.asset?.name ||
                                  'N/A'}
                              </div>
                              {device.cloneData &&
                              device.asset?.assetId !==
                                device.cloneData.assetId ? (
                                <div style={{ fontSize: 10, opacity: 0.5 }}>
                                  Original Asset: {device.asset?.name}
                                </div>
                              ) : (
                                false
                              )}
                            </DeviceMobileInfoValue>
                          </DeviceMobileInfoItem>
                          <DeviceMobileInfoItem>
                            <DeviceMobileInfoLabel>Type</DeviceMobileInfoLabel>
                            <DeviceMobileInfoValue>
                              {device.deviceType?.name || 'N/A'}
                            </DeviceMobileInfoValue>
                          </DeviceMobileInfoItem>
                          <DeviceMobileInfoItem>
                            <DeviceMobileInfoLabel>Status</DeviceMobileInfoLabel>
                            <DeviceMobileInfoValue>
                              <Tag
                                color={
                                  device.status === 'active'
                                    ? 'success'
                                    : 'error'
                                }
                              >
                                {device.status?.toUpperCase()}
                              </Tag>
                            </DeviceMobileInfoValue>
                          </DeviceMobileInfoItem>
                        </DeviceMobileInfoGrid>
                      </DeviceMobileCardBody>

                      {mobileActions?.length ? (
                        <Dropdown
                          trigger={['click']}
                          menu={{
                            items: mobileActions,
                            onClick: handleMobileActionClick(device),
                          }}
                        >
                          <DeviceMobileMoreButton
                            type="button"
                            aria-label={`Open actions for ${device.name}`}
                          >
                            <RiMore2Line />
                          </DeviceMobileMoreButton>
                        </Dropdown>
                      ) : null}
                    </DeviceMobileCardMain>

                    <DeviceMobileDetailButton
                      type="button"
                      onClick={() => {
                        setSearchParams({ tab: '1' });
                        navigate('/device/' + device.deviceId + '?tab=1');
                      }}
                    >
                      View Detail
                    </DeviceMobileDetailButton>
                    {isUserHasPermission([FEATURE + '.DETAIL']) && (
                      <DeviceMobileDataLogButton
                        type="button"
                        onClick={() => handleViewDataLog(device)}
                      >
                        Data Log
                      </DeviceMobileDataLogButton>
                    )}
                  </DeviceMobileCard>
                );
              })
            )}
          </DeviceMobileList>

          <DeviceMobilePagination>
            <DeviceMobilePaginationInfo>
              Showing {mobileStartResult}-{mobileEndResult} of {mobileTotal}
            </DeviceMobilePaginationInfo>
            <DeviceMobilePaginationActions>
              <DeviceMobilePageButton
                type="button"
                disabled={mobileCurrentPage <= 1}
                onClick={() => handleMobilePageChange(mobileCurrentPage - 1)}
              >
                <LeftOutlined />
              </DeviceMobilePageButton>
              <DeviceMobilePageButton type="button" $active>
                {mobileCurrentPage}
              </DeviceMobilePageButton>
              {mobileCurrentPage < mobileTotalPages && (
                <DeviceMobilePageButton
                  type="button"
                  onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
                >
                  {mobileCurrentPage + 1}
                </DeviceMobilePageButton>
              )}
              <DeviceMobilePageButton
                type="button"
                disabled={mobileCurrentPage >= mobileTotalPages}
                onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
              >
                <RightOutlined />
              </DeviceMobilePageButton>
            </DeviceMobilePaginationActions>
          </DeviceMobilePagination>
        </DeviceMobilePage>
      ) : (
        <>
          <HeaderSection
            title={'Device'}
            rightAction={[
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <Button type='primary' onClick={handleCreateDevice}>
                  Add Device
                </Button>
              ) : (
                false
              ),
            ]}
          />

          <ListFilterRow
            key={`device-filter-${isCompanyFilterReady}-${selectedCompanyFilter}-${isAssetFilterReady}-${selectedAssetFilter}`}
            filterValues={{
              search: pageQueries.search,
              status: pageQueries.filterStatus || 'all',
              filterData: pageQueries.filterArchived || 'default',
              asset: selectedAssetFilter,
              company: selectedCompanyFilter,
            }}
            filters={[filterData]}
          />

          <AppTable
            size='small'
            isLoading={isLoading}
            keyId='deviceId'
            columns={columns}
            data={data}
            pagination={pagination}
            onChangePage={changePage}
            onChangeLimit={changeLimit}
            // _table={{
            //   scroll:{ x: '100%' }
            // } as any}
          />
        </>
      )}

      {isMobile && (
        <DeviceMobileFilterDrawer
          title="Filter"
          placement="bottom"
          height="auto"
          open={isMobileFilterOpen}
          onClose={() => setIsMobileFilterOpen(false)}
        >
          <DeviceMobileFilterForm>
            <DeviceMobileFilterFields>
              <DeviceMobileFilterField>
                Company
                <DeviceMobileFilterSelect
                  value={mobileFilterValues.company}
                  options={filterCompanyOptions}
                  onChange={(value) => {
                    const nextValue = String(value);
                    setCompanyId(nextValue);
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      company: nextValue,
                      asset: 'all',
                    }));
                  }}
                />
              </DeviceMobileFilterField>
              <DeviceMobileFilterField>
                Asset
                <DeviceMobileFilterSelect
                  value={mobileFilterValues.asset}
                  options={filterAssetOptions}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      asset: String(value),
                    }))
                  }
                />
              </DeviceMobileFilterField>
              <DeviceMobileFilterField>
                Status
                <DeviceMobileFilterSelect
                  value={mobileFilterValues.status}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      status: String(value),
                    }))
                  }
                />
              </DeviceMobileFilterField>
              {isUserHasPermission([FEATURE + '.DELETE']) && (
                <DeviceMobileFilterField>
                  Filter Data
                  <DeviceMobileFilterSelect
                    value={mobileFilterValues.filterData}
                    options={[
                      { value: 'default', label: 'Default' },
                      { value: 'show', label: 'Include Archive Data' },
                    ]}
                    onChange={(value) =>
                      setMobileFilterValues((prev) => ({
                        ...prev,
                        filterData: String(value),
                      }))
                    }
                  />
                </DeviceMobileFilterField>
              )}
            </DeviceMobileFilterFields>
            <DeviceMobileFilterActions>
              <DeviceMobileFilterApplyButton
                type="primary"
                onClick={handleApplyMobileFilter}
              >
                Apply
              </DeviceMobileFilterApplyButton>
            </DeviceMobileFilterActions>
          </DeviceMobileFilterForm>
        </DeviceMobileFilterDrawer>
      )}

      <Modal
        title={
          forceDeleteId ? 'Force Delete Confirmation' : 'Delete Confirmation'
        }
        open={isModalDeleteOpen}
        onOk={deleteData}
        okButtonProps={{
          style: {
            background: 'red',
          },
        }}
        onCancel={handleCancelDelete}
      >
        {forceDeleteId ? (
          <p>
            Are you sure want to permanently delete this data? This data will be
            deleted and only can't be undo.
          </p>
        ) : (
          <p>
            Are you sure want to delete this data? This data will be archived
            and only User with restore permission can undo this action.
          </p>
        )}
      </Modal>

      <Modal
        title='Restore Confirmation'
        open={isModalRestoreOpen}
        onOk={restoreData}
        okButtonProps={{
          style: {
            background: 'blue',
          },
        }}
        onCancel={handleCancelDelete}
      >
        <p>Are you sure want to restore this data?.</p>
      </Modal>
    </React.Fragment>
  );
};
export default Device;
